import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./map.module.css";
import Spinner from "../Spinner";

function RadiusCoordinatedMap({ 
  coordinates, 
  mapLoading: loadingProp, 
  circleColor = "#2563eb", 
  isEditing, 
  onMapClick, 
  setLatitude, 
  setLongitude,   
  latInputRef,    
  lngInputRef    
 }) {

  const [mapLoading, setMapLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const circleRef = useRef(null);
  const mapClickListenerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);


  const addRadius = useCallback((map, coordinate, radius, color = "#2563eb") => {
    try {
      // Remove círculo anterior se existir
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }

      circleRef.current = new window.google.maps.Circle({
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35,
        map,
        center: coordinate,
        radius: Number(radius) || 50,
      });
    } catch (error) {
      console.error("Erro ao adicionar o radius", error);
    }
  }, []);

  const createMap = useCallback((coords) => {

    // Verifica se o Google Maps está carregado
    if (!window.google || !window.google.maps) {
      console.warn("Google Maps API não está carregada");
      return;
    }

    // Verifica se o elemento do mapa existe no DOM
    const mapElement = mapRef.current;
    if (!mapElement) {
      console.warn("Elemento do mapa não encontrado no DOM");
      return;
    }

    // Limpa mapa anterior se existir
    if (mapInstanceRef.current) {
      // Remove todos os listeners e limpa o mapa
      window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      mapInstanceRef.current = null;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: 'Brazil' }, function (results, status) {

      if (status === window.google.maps.GeocoderStatus.OK) {
        // Verifica novamente se o elemento ainda existe (pode ter sido removido durante o geocode)
        if (!mapRef.current) {
          console.warn("Elemento do mapa foi removido durante a inicialização");
          return;
        }

        let center = getSavedMapCenter();
        let zoom = getSavedMapZoom();

        if (coords) {
          center = { lat: Number(coords.latitude), lng: Number(coords.longitude) };
          const radiusInMeters = Number(coords.radius) || 50;

          // --- CORREÇÃO DA LÓGICA DE ZOOM ---
          if (radiusInMeters <= 50) {
            zoom = 18; // Zoom bem próximo para raios pequenos
          } else if (radiusInMeters <= 150) {
            zoom = 17;
          } else if (radiusInMeters <= 300) {
            zoom = 16;
          } else if (radiusInMeters <= 500) {
            zoom = 15;
          } else if (radiusInMeters <= 1000) {
            zoom = 14;
          } else {
            zoom = 13; // Para raios muito grandes
          }
          // --- FIM DA CORREÇÃO ---
        }

        try {
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: center ?? results[0].geometry.location,
            zoom: zoom ?? 4,
            streetViewControl: false,
          });

          setMapReady(true);

          mapInstanceRef.current.addListener('dragend', function () {
            const center = mapInstanceRef.current.getCenter();
            document.cookie = `coordinateMapCenter=${center.lat()},${center.lng()}`;
          });

          mapInstanceRef.current.addListener('zoom_changed', function () {
            const zoom = mapInstanceRef.current.getZoom();
            document.cookie = `coordinateMapZoom=${zoom}`;
          });

          if (coords && coords.latitude && coords.longitude) {
            addRadius(mapInstanceRef.current, { lat: Number(coords.latitude), lng: Number(coords.longitude) }, coords.radius, circleColor);
          }
        } catch (error) {
          console.error("Erro ao criar o mapa:", error);
        }
      }
    });
  }, [addRadius, circleColor]); // Adicione addRadius e circleColor como dependências do useCallback

  useEffect(() => {
    console.log("📍 useEffect de criação do mapa - coordinates:", coordinates);

    const timer = setTimeout(() => {
      if (mapRef.current) {
        console.log("✅ mapRef.current existe, chamando createMap");
        if (coordinates) {
          createMap(coordinates);
        } else {
          createMap(null);
        }
      } else {
        console.log("❌ mapRef.current é null");
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [coordinates, createMap]);


  const getSavedMapCenter = () => {
    const name = 'coordinateMapCenter=';
    const ca = decodeURIComponent(document.cookie).split(';');
    for (let c of ca) {
      c = c.trim();
      if (c.startsWith(name)) {
        const latLng = c.substring(name.length).split(',');
        return { lat: parseFloat(latLng[0]), lng: parseFloat(latLng[1]) };
      }
    }
    return null;
  };

  const getSavedMapZoom = () => {
    const name = 'coordinateMapZoom=';
    const ca = decodeURIComponent(document.cookie).split(';');
    for (let c of ca) {
      c = c.trim();
      if (c.startsWith(name)) {
        return parseInt(c.substring(name.length));
      }
    }
    return null;
  };

  useEffect(() => {
    setMapLoading(!!loadingProp);
  }, [loadingProp]);

  // ✅ Função de clique com log BEM VISÍVEL
  const handleMapClick = useCallback((e) => {
    console.log("🎯🎯🎯 CLIQUE NO MAPA DETECTADO! 🎯🎯🎯");
    console.log("Evento:", e);
    console.log("latLng:", e.latLng);

    if (!e.latLng) {
      console.log("❌ latLng é null");
      return;
    }

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    console.log(`✅ Coordenadas: ${lat}, ${lng}`);
    console.log("mapInstanceRef.current:", mapInstanceRef.current);
    console.log("circleColor:", circleColor);
    console.log("coordinates?.radius:", coordinates?.radius);

    // Move o círculo
    addRadius(
      mapInstanceRef.current,
      { lat, lng },
      coordinates?.radius || 50,
      circleColor
    );

    // Callback
    onMapClick && onMapClick({ lat, lng });

    console.log("✅ Círculo movido!");
  }, [onMapClick, addRadius, circleColor, coordinates]);

  useEffect(() => {
    console.log("📍 useEffect de clique - isEditing:", isEditing, "mapReady:", mapReady);

    if (!isEditing || !mapReady) {
      console.log("❌ Aguardando: isEditing ou mapa não criado ainda");
      return;
    }

    console.log("✅ Registrando listener de clique");

    const handleMapClick = (e) => {

      console.log("🎯 CLIQUE NO MAPA DETECTADO!");

      if (!e.latLng) {
        console.log("❌ latLng não disponível");
        return;
      }

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setLatitude(lat)
      setLongitude(lng)

      if (latInputRef?.current) {
        latInputRef.current.value = lat;
      }
      if (lngInputRef?.current) {
        lngInputRef.current.value = lng;
      }
  

      // Move o círculo
      addRadius(
        mapInstanceRef.current,
        { lat, lng },
        coordinates?.radius || 50,
        circleColor
      );

      // Callback
      onMapClick && onMapClick({ lat, lng });
    };

    mapClickListenerRef.current = window.google.maps.event.addListener(
      mapInstanceRef.current,
      "click",
      handleMapClick
    );

    console.log("✅ Listener registrado com sucesso!");

    return () => {
      console.log("🧹 Removendo listener");
      if (mapClickListenerRef.current) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }
    };
  }, [isEditing, mapReady, addRadius, circleColor, coordinates, onMapClick, setLatitude, setLongitude]);


  // só recria o mapa se realmente tiver coordenadas novas e o elemento estiver no DOM
  useEffect(() => {
    // Aguarda um pouco para garantir que o elemento está no DOM
    const timer = setTimeout(() => {
      if (mapRef.current) {
        if (coordinates) {
          createMap(coordinates);
        } else {
          createMap(null);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [coordinates, createMap]);

  // Limpeza quando o componente é desmontado
  useEffect(() => {
    return () => {
      // Remove círculo
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      // Limpa listeners do mapa
      if (mapInstanceRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(mapInstanceRef.current);
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <div ref={mapRef} className={styles.visitControlMap} id="map"></div>
      {mapLoading && <div className={styles.mapOverlay}><Spinner /></div>}
    </>
  );
}

export default React.memo(RadiusCoordinatedMap);